import { yupResolver } from "@hookform/resolvers/yup";
import AgreementSignZoneModal from "components/AgreementSignZone/AgreementSignZoneModal";
import LoadingFormNormal from "components/shared-components/LoadingFormNormal";

import {
  AGREEMENT_BLOCK_TYPE,
  AGREEMENT_FIELD,
  AGREEMENT_ITEM_IDS_FIELD,
  AGREEMENT_ITEM_QUANTITIES_FIELD,
  AGREEMENT_PARTY_A_TYPE,
  AGREEMENT_PENDING_SIGN_STATUSES,
  AGREEMENT_REPORT_TITLE,
  getAllFieldsByType,
  getFieldBlocksByType,
} from "constants/AgreementReportConstant";
import { TYPE_REPORT_WORD } from "constants/IssueInvoiceConstant";
import { english } from "flatpickr/dist/l10n/default";
import { Vietnamese } from "flatpickr/dist/l10n/vn";
import "flatpickr/dist/themes/material_blue.css";
import { loopGenerateField } from "helpers/form_helper_license";
import {
  useGetAgreementInfo,
  useGetAgreementList,
  usePreviewAgreementGra,
  useSaveAgreementInfo,
} from "hooks/invoiceAgreement/useInvoiceAgreement";
import {
  apiToFormValues,
  buildFormSchema,
  buildReturnItems,
  formValuesToApi,
  validateReturnQuantity,
} from "pages/IssueInvoice/helper/agreementReport";
import {
  buildAgreementOptionLabel,
  formatAgreementNumber,
  getPendingAgreements,
} from "pages/IssueInvoice/helper/agreementPending";
import AgreementItemsTable from "pages/IssueInvoice/modal/AgreementItemsTable";
import AgreementPdfModal from "pages/IssueInvoice/modal/AgreementPdfModal";
import AgreementSignPaperModal from "pages/IssueInvoice/modal/AgreementSignPaperModal";
import {
  fixConvertNumberToText,
  formatTienFinal,
  initOptionDateLocale,
} from "pages/IssueInvoice/utils";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useToasts } from "react-toast-notifications";
import {
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";

// API biên bản không trả về đơn vị tiền tệ, số tiền lấy từ hóa đơn gốc luôn là VND
const CURRENCY_CODE = "VND";

// Gắn lên khối bọc mỗi field để cuộn tới được ô nhập còn thiếu khi lưu.
// Phải bám theo class vì input số dựng bằng NumberFormat, không có id/name.
const FIELD_ANCHOR_PREFIX = "agreement-field-";

// Chờ người dùng gõ xong số lượng trả mới hỏi BE tính lại tiền
const DELAY_PREVIEW_QUANTITY = 500;

// Việc cần làm tiếp sau khi lưu biên bản thành công
const AFTER_SAVE_ACTION = {
  PDF: "pdf",
  SIGN_PAPER: "sign_paper",
  SIGN_ZONE: "sign_zone",
};

const AgreementReportModal = props => {
  const { isOpen, toggle, invoiceId, typeReport, onCreateSuccess } = props;

  const { t, i18n } = useTranslation(["issueInvoice"]);

  const { addToast } = useToasts();

  const [rawData, setRawData] = useState(null);
  // Một loại lập được nhiều lần: các bản nháp / ký 1 bên để chọn xem lại, và
  // bản đang mở (null = đang lập bản mới)
  const [pendingAgreements, setPendingAgreements] = useState([]);
  const [selectedAgreementId, setSelectedAgreementId] = useState(null);
  // Đọc trong onSuccess để bỏ kết quả của bản đã chuyển đi chỗ khác
  const selectedAgreementIdRef = useRef(null);
  // Biên bản trả hàng: thành tiền từng dòng BE vừa tính lại { [id]: amount }
  const [previewAmountsById, setPreviewAmountsById] = useState({});
  // Biên bản trả hàng: câu lỗi BE trả về (result: false), hiện dưới bảng hàng
  const [itemsError, setItemsError] = useState("");
  const previewTimerRef = useRef(null);
  const [optionDateLocale, setOptionDateLocale] =
    useState(initOptionDateLocale);
  const [modalAgreementPdf, setModalAgreementPdf] = useState(false);
  const toggleAgreementPdfModal = () =>
    setModalAgreementPdf(!modalAgreementPdf);
  const [modalSignPaper, setModalSignPaper] = useState(false);
  const toggleSignPaperModal = () => setModalSignPaper(!modalSignPaper);
  const [modalSignZone, setModalSignZone] = useState(false);
  const toggleSignZoneModal = () => setModalSignZone(!modalSignZone);
  // Bấm "Lưu và Xem PDF" / "In ký giấy" / "Ký số": lưu lại theo dữ liệu đang
  // nhập trước, lưu xong (result: true) và nạp lại dữ liệu mới mở modal tương ứng
  const afterSaveActionRef = useRef(null);
  // Bản state của afterSaveActionRef để nút giữ spinner tới lúc modal mở
  const [pendingAction, setPendingAction] = useState(null);
  const setAfterSaveAction = action => {
    afterSaveActionRef.current = action;
    setPendingAction(action);
  };
  const openModalByAction = {
    [AFTER_SAVE_ACTION.PDF]: () => setModalAgreementPdf(true),
    [AFTER_SAVE_ACTION.SIGN_PAPER]: () => setModalSignPaper(true),
    [AFTER_SAVE_ACTION.SIGN_ZONE]: () => setModalSignZone(true),
  };

  const blocks = useMemo(() => getFieldBlocksByType(typeReport), [typeReport]);

  const { schema, defaultValues } = useMemo(
    () => buildFormSchema(getAllFieldsByType(typeReport), t),
    [typeReport, t]
  );

  // Danh sách field dựng động theo loại biên bản nên TypeScript chỉ suy được 3
  // key khai tường minh bên dưới, khiến mọi watch/setValue theo tên field động
  // bị báo sai kiểu. Ép kiểu về UseFormReturn<any> để bỏ cảnh báo đó.
  const mainForm = /** @type {import("react-hook-form").UseFormReturn<any>} */ (
    useForm({
      resolver: yupResolver(schema),
      defaultValues: {
        ...defaultValues,
        agreement_id: null,
        pdf_status: null,
        sign_method: null,
      },
    })
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, touchedFields },
  } = mainForm;

  const { mutate: mutateGetAgreementInfo, isLoading: isLoadingGet } =
    useGetAgreementInfo({
      onSuccess: ({ data: agreementInfo, variables }) => {
        const requestedId = variables?.agreement_id || null;
        // Người dùng đã chọn bản khác trong lúc chờ
        if (requestedId !== selectedAgreementIdRef.current) {
          return;
        }

        let agreementData = agreementInfo;
        console.log("AgreementReportModal mutateGetAgreementInfo", {
          agreementData,
        });
        agreementData = {
          ...agreementData,
          sign_method_title: agreementData?.sign_method
            ? t(`form.sign_method_agreement.${agreementData?.sign_method}`)
            : "",
        };
        setRawData(agreementData);
        reset(apiToFormValues(agreementData, typeReport));
        // Dữ liệu mới đã có thành tiền theo số lượng đã lưu
        clearTimeout(previewTimerRef.current);
        setPreviewAmountsById({});
        setItemsError("");

        // Đã có dữ liệu mới thì mới mở modal đang chờ
        const action = afterSaveActionRef.current;
        setAfterSaveAction(null);
        action && openModalByAction[action]?.();
      },
      onError: () => {
        setAfterSaveAction(null);
      },
    });

  // Mở một bản đã lập, hoặc agreementId = null để lập bản mới
  const loadAgreement = agreementId => {
    selectedAgreementIdRef.current = agreementId || null;
    setSelectedAgreementId(agreementId || null);
    mutateGetAgreementInfo({
      id: invoiceId,
      type: typeReport,
      agreement_id: agreementId || undefined,
    });
  };

  const { mutate: mutateGetAgreementList, isLoading: isLoadingList } =
    useGetAgreementList({
      onSuccess: response => {
        const items = response.data?.items || [];
        setPendingAgreements(getPendingAgreements(items, typeReport));

        // Nạp lại sau khi lưu / ký thì giữ bản đang mở; mới mở modal thì
        // chọn bản dở đầu tiên, chưa có bản nào thì vào lập mới
        loadAgreement(
          selectedAgreementIdRef.current ||
            getPendingAgreements(items, typeReport)[0]?.agreement_id
        );
      },
      onError: () => {
        loadAgreement(selectedAgreementIdRef.current);
      },
    });

  const refreshAgreementList = () =>
    mutateGetAgreementList({
      id: invoiceId,
      sign_status: AGREEMENT_PENDING_SIGN_STATUSES.join(","),
    });

  const { mutate: mutateSaveAgreementInfo, isLoading: isLoadingSave } =
    useSaveAgreementInfo({
      onSuccess: ({ data }) => {
        // Interceptor không chặn result: false (vẫn vào onSuccess) -> tự kiểm
        // tra, không đạt thì hủy việc mở modal tiếp theo. So === false chứ
        // không !== true: useMutationCommon trả body.data khi có, lúc đó cờ
        // result nằm ở body nên không đọc được (theo khuôn CreateStatement).
        // Không đạt thì cũng giữ nguyên dữ liệu đang nhập, không nạp lại.
        if (data?.result === false) {
          setAfterSaveAction(null);
          setItemsError(data?.message || "");
          data?.message &&
            addToast(data.message, { appearance: "error", autoDismiss: true });
          return;
        }

        setItemsError("");
        // Lưu kèm mở modal (PDF / ký) thì modal mở lên đã là báo thành công
        if (!afterSaveActionRef.current) {
          addToast(
            t("message.saveAgreementInfo", {
              target: t("agreementReport"),
              status: t("message.success"),
            }),
            { appearance: "success", autoDismiss: true }
          );
        }

        // agreement-save trả agreement_id của bản vừa lưu (bản mới hay bản
        // đang sửa) -> giữ bản đó, nạp lại danh sách cho dropdown rồi nạp bản đó
        selectedAgreementIdRef.current =
          data?.agreement_id || selectedAgreementIdRef.current;
        refreshAgreementList();
        onCreateSuccess && onCreateSuccess();
      },
      onError: () => {
        setAfterSaveAction(null);
      },
    });

  // Biên bản trả hàng: BE tính lại tiền theo các dòng còn giữ, FE không tự tính
  const { mutate: mutatePreviewGra, isLoading: isLoadingPreview } =
    usePreviewAgreementGra({
      onSuccess: ({ data }) => {
        // Số lượng trả không hợp lệ: BE trả result false kèm câu báo lỗi
        if (data?.result === false) {
          setItemsError(data?.message || "");
          data?.message &&
            addToast(data.message, { appearance: "error", autoDismiss: true });
          return;
        }

        setItemsError("");

        // useMutationCommon trả về phần data của body; body có thể lồng thêm data
        const previewItems = data?.data?.items || data?.items || [];
        setPreviewAmountsById(
          previewItems.reduce(
            (result, item) => ({ ...result, [Number(item.id)]: item.amount }),
            {}
          )
        );

        const after = data?.data?.amounts?.after || data?.amounts?.after;

        if (!after) {
          return;
        }

        setValue(AGREEMENT_FIELD.AMOUNT_AFTER.field, Number(after.amount || 0));
        setValue(AGREEMENT_FIELD.TAX_AFTER.field, Number(after.tax || 0));
        setValue(AGREEMENT_FIELD.TOTAL_AFTER.field, Number(after.total || 0));
        setValue(
          AGREEMENT_FIELD.TOTAL_IN_WORDS_AFTER.field,
          after.total_in_words || ""
        );
      },
    });

  // Dòng đang giữ có số lượng trả không hợp lệ (đã báo ngay dưới ô nhập)
  const hasInvalidQuantity = (keptIds, quantities) =>
    (rawData?.items || []).some(
      item =>
        keptIds.map(Number).includes(Number(item?.id)) &&
        validateReturnQuantity(
          quantities?.[Number(item.id)] ?? item.quantity,
          item.quantity
        )
    );

  // Hỏi BE số tiền mới theo dòng giữ lại + số lượng trả. Số lượng còn sai thì
  // chưa hỏi, chờ người dùng sửa. Lỗi thì hook báo toast, số tiền cũ giữ nguyên.
  const previewGra = (keptIds, quantities) => {
    clearTimeout(previewTimerRef.current);

    if (hasInvalidQuantity(keptIds, quantities)) {
      return;
    }

    // Bắt đầu hỏi lại BE thì bỏ lỗi của lần trước
    setItemsError("");
    mutatePreviewGra({
      invoice_id: invoiceId,
      item_ids: keptIds,
      items: buildReturnItems(rawData?.items, keptIds, quantities),
    });
  };

  // Xóa / hoàn tác một dòng hàng: lưu lại lựa chọn rồi tính lại tiền ngay
  const handleChangeItemIds = nextItemIds => {
    setValue(AGREEMENT_ITEM_IDS_FIELD, nextItemIds);
    previewGra(nextItemIds, itemQuantities);
  };

  // Sửa số lượng trả: chờ gõ xong mới tính lại tiền
  const handleChangeQuantity = (itemId, value) => {
    const nextQuantities = { ...itemQuantities, [Number(itemId)]: value };
    setValue(AGREEMENT_ITEM_QUANTITIES_FIELD, nextQuantities);

    clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(
      () => previewGra(selectedItemIds, nextQuantities),
      DELAY_PREVIEW_QUANTITY
    );
  };

  // Dừng hẹn giờ tính tiền khi component bị gỡ
  useEffect(() => {
    return () => clearTimeout(previewTimerRef.current);
  }, []);

  // Đổi ngôn ngữ cho lịch
  useEffect(() => {
    setOptionDateLocale(prev => ({
      ...prev,
      locale: {
        ...(i18n.language === "vn" ? Vietnamese : english),
        firstDayOfWeek: prev.locale.firstDayOfWeek,
      },
    }));
  }, [i18n.language]);

  // Tổng tiền thanh toán = tiền chưa thuế + tiền thuế GTGT, và số tiền bằng chữ
  // đọc từ chính tổng đó. Cả hai đều tự tính, người dùng không nhập tay.
  // Tính chung một chỗ để hai ô luôn khớp nhau và chỉ chờ một nhịp debounce.
  const [amountAfter, taxAfter] = watch([
    AGREEMENT_FIELD.AMOUNT_AFTER.field,
    AGREEMENT_FIELD.TAX_AFTER.field,
  ]);

  useEffect(() => {
    // Loại biên bản không có block số tiền thì bỏ qua
    if (amountAfter === undefined && taxAfter === undefined) {
      return;
    }

    // Biên bản trả hàng lấy nguyên số của agreement-gra-preview, tự tính lại ở
    // đây sẽ ghi đè tổng tiền và tiền bằng chữ do BE trả về
    if (typeReport === TYPE_REPORT_WORD.GRA) {
      return;
    }

    // Chờ người dùng gõ xong mới tính
    const delayTotalFn = setTimeout(() => {
      const total = Number(amountAfter || 0) + Number(taxAfter || 0);
      setValue(AGREEMENT_FIELD.TOTAL_AFTER.field, total);

      let totalInWords = fixConvertNumberToText(
        formatTienFinal(total, CURRENCY_CODE)
      );
      totalInWords =
        totalInWords.charAt(0).toUpperCase() + totalInWords.slice(1);

      setValue(AGREEMENT_FIELD.TOTAL_IN_WORDS_AFTER.field, totalInWords);
    }, 500);

    return () => clearTimeout(delayTotalFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountAfter, taxAfter]);

  // Biên bản điều chỉnh thông tin: đang điều chỉnh thông tin bên nào.
  // Ô của bên kia ẩn đi và không bị bắt buộc nhập.
  const adjustParty = watch(AGREEMENT_FIELD.ADJUST_PARTY.field);
  const selectedItemIds = watch(AGREEMENT_ITEM_IDS_FIELD) || [];
  const itemQuantities = watch(AGREEMENT_ITEM_QUANTITIES_FIELD) || {};

  const isFieldVisible = item =>
    !item.visibleWhen || String(adjustParty || "") === item.visibleWhen.is;

  // Loại bên mua đang chọn trên radio (chưa cần lưu). Chỉ doanh nghiệp mới ký số;
  // cá nhân chỉ ký giấy nên ẩn nút "Ký số" ngay khi đổi radio.
  const partyAType = watch(AGREEMENT_FIELD.PARTY_A_TYPE.field);
  const isOrganizationBuyer =
    Number(partyAType) === AGREEMENT_PARTY_A_TYPE.ORGANIZATION;

  // Modal ký số lưu lại biên bản từ rawData (bản đã lưu) -> ghép lựa chọn radio
  // hiện tại vào, nếu không lần lưu đó sẽ ghi đè về loại cũ và backend từ chối ký.
  const rawDataForSign = useMemo(
    () =>
      rawData && {
        ...rawData,
        party_a: { ...rawData.party_a, type: Number(partyAType) || null },
      },
    [rawData, partyAType]
  );

  // Mở modal thì nạp danh sách bản dở rồi mới nạp dữ liệu, đóng modal thì xóa sạch
  useEffect(() => {
    selectedAgreementIdRef.current = null;
    setSelectedAgreementId(null);
    setPendingAgreements([]);

    if (isOpen && invoiceId) {
      refreshAgreementList();
    } else {
      setRawData(null);
      setItemsError("");
      setAfterSaveAction(null);
      reset(defaultValues);
    }
  }, [isOpen]);

  // Bản đang mở có thể vừa ký xong nên không còn trong danh sách bản dở ->
  // vẫn hiện nó trong dropdown theo dữ liệu đang xem
  const agreementOptions = useMemo(() => {
    const options = pendingAgreements.map(item => ({
      value: item.agreement_id,
      label: buildAgreementOptionLabel(item),
    }));

    if (
      rawData?.agreement_id &&
      !options.some(option => option.value === rawData.agreement_id)
    ) {
      options.unshift({
        value: rawData.agreement_id,
        label: buildAgreementOptionLabel(rawData),
      });
    }

    return options;
  }, [pendingAgreements, rawData]);

  const selectedAgreementOption =
    agreementOptions.find(option => option.value === selectedAgreementId) ||
    null;

  // Đang lưu, hoặc đã lưu xong nhưng còn nạp lại dữ liệu trước khi mở modal
  const isBusy = isLoadingSave || Boolean(pendingAction);

  // Nút đang chờ mở modal thì hiện spinner thay icon
  const renderActionIcon = (action, iconClass) =>
    pendingAction === action ? (
      <i className="bx bx-loader bx-spin label-icon" />
    ) : (
      <i className={`${iconClass} label-icon`}></i>
    );

  const submitForm = values => {
    // Số lượng trả sai thì không lưu: lỗi đã hiện ngay dưới ô nhập
    if (
      typeReport === TYPE_REPORT_WORD.GRA &&
      hasInvalidQuantity(
        values[AGREEMENT_ITEM_IDS_FIELD] || [],
        values[AGREEMENT_ITEM_QUANTITIES_FIELD]
      )
    ) {
      setAfterSaveAction(null);
      addToast("Vui lòng nhập đúng số lượng trả của các dòng hàng hóa", {
        appearance: "info",
        autoDismiss: true,
      });
      return;
    }

    const dataSave = {
      invoice_id: invoiceId,
      type: typeReport,
      agreement_id: rawData?.agreement_id,
      data: formValuesToApi(values, rawData, typeReport),
    };
    console.log("AgreementReportModal submitForm", {
      dataSave,
    });
    setItemsError("");
    mutateSaveAgreementInfo(dataSave);
  };

  // Lưu lại biên bản theo dữ liệu đang nhập rồi mới mở modal của action.
  // Form không hợp lệ thì submitFormInvalid hủy việc mở modal.
  const saveThenOpen = action => {
    setAfterSaveAction(action);
    handleSubmit(submitForm, submitFormInvalid)();
  };

  // Form không hợp lệ: liệt kê ô còn thiếu rồi cuộn tới ô đầu tiên.
  // Duyệt theo thứ tự khai báo field để danh sách trùng thứ tự nhìn thấy trên
  // màn hình, thay vì thứ tự khóa của object errors.
  const submitFormInvalid = formErrors => {
    setAfterSaveAction(null);

    const invalidFields = getAllFieldsByType(typeReport).filter(
      item => formErrors?.[item.field]
    );

    if (invalidFields.length === 0) {
      return;
    }

    const labels = invalidFields.map(item =>
      t(`form.${item.labelKey || item.field}`)
    );
    addToast(`Vui lòng nhập đủ thông tin cần thiết: ${labels.join("\n ")}`, {
      appearance: "info",
      autoDismiss: true,
    });

    const firstField = document.querySelector(
      `.${FIELD_ANCHOR_PREFIX}${invalidFields[0].field}`
    );
    firstField?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Người đại diện / chức vụ hai bên là lựa chọn của người lập nên sửa được ở
  // mọi loại biên bản, kể cả loại khóa thông tin theo hóa đơn
  const EDITABLE_FIELDS = [
    AGREEMENT_FIELD.REASON.field,
    AGREEMENT_FIELD.PARTY_A_TYPE.field,
    AGREEMENT_FIELD.PARTY_A_AFTER_REPRESENTATIVE.field,
    AGREEMENT_FIELD.PARTY_A_AFTER_POSITION.field,
    AGREEMENT_FIELD.PARTY_B_REPRESENTATIVE.field,
    AGREEMENT_FIELD.PARTY_B_POSITION.field,
  ];

  // Biên bản trả hàng bám nguyên theo hóa đơn nên khoá hết ô nhập, trừ các ô
  // người lập tự nhập
  const isReadOnlyField = item =>
    typeReport === TYPE_REPORT_WORD.GRA &&
    !EDITABLE_FIELDS.includes(item.field);

  const generalField = item => {
    const disableForm =
      isLoadingSave ||
      item.disable ||
      rawData?.sign_method ||
      isReadOnlyField(item);
    return loopGenerateField({
      item: {
        ...item,
        // Bỏ gạch chân khi chỉ để xem — xử lý bằng class vì ô ngày của
        // flatpickr không nhận inline style
        classForm: `${
          disableForm ? "custom-form-readonly" : ""
        } ${FIELD_ANCHOR_PREFIX}${item.field}`,
      },
      // labelKey để 2 bộ before/after dùng chung một nhãn
      label: t(`form.${item.labelKey || item.field}`),
      placeholder: item.placeholder || "",
      errors,
      touchedFields,
      register,
      control,
      OPTION_GROUP: {},
      optionDateLocale,
      disableForm: disableForm,
      watch,
      setValue,
    });
  };

  // Nhóm "Đại diện các bên": nhãn nằm trên đầu cột, mỗi bên một dòng
  const renderRepresentativeRow = (label, fields) => (
    <Row className="align-items-start mb-1" key={label}>
      <Col xs="12" md="3" className="pt-2" style={{ fontWeight: "500" }}>
        {label}
      </Col>
      {fields.map(item =>
        generalField({ ...item, isShowLabel: false, widthLabel: null })
      )}
    </Row>
  );

  const renderRepresentativeBlock = block => {
    const [partyAName, partyAPosition, partyBName, partyBPosition] =
      block.fields;

    return (
      <React.Fragment>
        <Row className="d-none d-md-flex mb-1">
          <Col xs="3" />
          <Col style={{ fontWeight: "500" }}>
            {t(`form.${AGREEMENT_FIELD.PARTY_A_AFTER_REPRESENTATIVE.labelKey}`)}
            <span className="text-danger"> (*)</span>
          </Col>
          <Col style={{ fontWeight: "500" }}>
            {t(`form.${AGREEMENT_FIELD.PARTY_A_AFTER_POSITION.labelKey}`)}
            <span className="text-danger"> (*)</span>
          </Col>
        </Row>
        {renderRepresentativeRow("Bên A (Bên mua)", [
          { ...partyAName, placeholder: "Họ và tên" },
          { ...partyAPosition, placeholder: "Giám đốc" },
        ])}
        {renderRepresentativeRow("Bên B (Bên bán)", [
          { ...partyBName, placeholder: "Họ và tên" },
          { ...partyBPosition, placeholder: "Giám đốc" },
        ])}
      </React.Fragment>
    );
  };

  const renderBlockBody = block => {
    if (block.type === AGREEMENT_BLOCK_TYPE.ITEMS) {
      return (
        <AgreementItemsTable
          items={rawData?.items}
          selectedIds={selectedItemIds}
          onChangeSelected={handleChangeItemIds}
          quantities={itemQuantities}
          onChangeQuantity={handleChangeQuantity}
          amountsById={previewAmountsById}
          error={itemsError}
          isDisabled={
            isLoadingSave || isLoadingPreview || Boolean(rawData?.sign_method)
          }
          isReadOnly={Boolean(rawData?.sign_method)}
        />
      );
    }

    if (block.type === AGREEMENT_BLOCK_TYPE.REPRESENTATIVES) {
      return renderRepresentativeBlock(block);
    }

    return (
      <Row className={block.rowClass}>
        {block.fields.filter(isFieldVisible).map(item => generalField(item))}
      </Row>
    );
  };

  const tiltleMode = !rawData?.agreement_id
    ? "Lập"
    : !rawData?.sign_method
    ? "Điều chỉnh"
    : "Xem";

  return (
    <Modal
      size="lg"
      style={{ maxWidth: "80%" }}
      isOpen={isOpen}
      role="dialog"
      autoFocus={true}
      centered={true}
      className="exampleModal"
      toggle={toggle}
    >
      <div className="modal-content">
        <ModalHeader toggle={toggle}>
          <div className="d-flex gap-1 align-items-center">
            <div>{tiltleMode}</div>
            <div
              className="text-primary fw-bold"
              style={{
                fontStyle: "italic",
              }}
            >
              {AGREEMENT_REPORT_TITLE[typeReport]}
            </div>
            {/* Đã lưu thì ghi rõ đang mở bản nào (một loại lập được nhiều lần) */}
            {rawData?.agreement_id && (
              <div className="fw-bold">- {formatAgreementNumber(rawData)}</div>
            )}
          </div>
        </ModalHeader>

        <ModalBody>
          <div
            style={{
              maxHeight: "70vh",
              overflow: "auto",
              paddingLeft: "20px",
              paddingRight: "20px",
            }}
          >
            {agreementOptions.length > 0 && (
              <Row className="align-items-center mb-3">
                <Col xs="12" md="6">
                  <Select
                    classNamePrefix="select2-selection"
                    isSearchable={true}
                    noOptionsMessage={() => "Không tìm thấy biên bản"}
                    placeholder="Đang lập biên bản mới"
                    options={agreementOptions}
                    value={selectedAgreementOption}
                    isDisabled={isLoadingGet || isLoadingList || isLoadingSave}
                    onChange={option => loadAgreement(option?.value)}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </Col>
                <Col xs="auto">
                  {selectedAgreementId && (
                    <Button
                      type="button"
                      color="primary"
                      outline
                      className="btn-label"
                      disabled={isLoadingGet || isLoadingList || isLoadingSave}
                      onClick={() => loadAgreement(null)}
                    >
                      <i className="fas fa-plus label-icon"></i>
                      Tạo mới
                    </Button>
                  )}
                </Col>
              </Row>
            )}
            {blocks.map(block => (
              <React.Fragment key={block.title}>
                <div className="custom-form-heading d-flex align-items-center">
                  <i className="bx bx-info-circle text-primary"></i>
                  <strong> {block.title} </strong>
                </div>
                <Card
                  style={{
                    border: "1px solid #d4d4d4",
                    zIndex: "3",
                  }}
                >
                  <CardBody>{renderBlockBody(block)}</CardBody>
                </Card>
              </React.Fragment>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            color="secondary"
            disabled={isBusy}
            onClick={toggle}
          >
            Đóng
          </Button>
          {!rawData?.sign_method && (
            <Button
              type="button"
              color="primary"
              className="btn-label"
              disabled={isBusy}
              onClick={handleSubmit(submitForm, submitFormInvalid)}
            >
              <i className="fas fa-save label-icon"></i>
              Lưu
            </Button>
          )}

          <Button
            type="button"
            color="danger"
            className="btn-label"
            disabled={isBusy}
            onClick={() => {
              // Đã ký thì biên bản không sửa được nữa, xem thẳng PDF
              if (rawData?.sign_method) {
                setModalAgreementPdf(true);
                return;
              }

              saveThenOpen(AFTER_SAVE_ACTION.PDF);
            }}
          >
            {renderActionIcon(AFTER_SAVE_ACTION.PDF, "fas fa-file-pdf")}
            {pendingAction === AFTER_SAVE_ACTION.PDF
              ? "Đang mở PDF..."
              : rawData?.sign_method
              ? "Xem biên bản đã ký"
              : "Lưu và Xem PDF"}
          </Button>
          {rawData?.agreement_id && !rawData?.sign_method && (
            <>
              <Button
                type="button"
                color="warning"
                className="btn-label"
                disabled={isBusy}
                onClick={() => saveThenOpen(AFTER_SAVE_ACTION.SIGN_PAPER)}
              >
                {renderActionIcon(
                  AFTER_SAVE_ACTION.SIGN_PAPER,
                  "fas fa-file-signature"
                )}
                {pendingAction === AFTER_SAVE_ACTION.SIGN_PAPER
                  ? "Đang mở..."
                  : "In ký giấy"}
              </Button>
              {typeReport !== TYPE_REPORT_WORD.GRA && isOrganizationBuyer && (
                <Button
                  type="button"
                  color="success"
                  className="btn-label"
                  disabled={isBusy}
                  onClick={() => saveThenOpen(AFTER_SAVE_ACTION.SIGN_ZONE)}
                >
                  {renderActionIcon(
                    AFTER_SAVE_ACTION.SIGN_ZONE,
                    "fas fa-file-signature"
                  )}
                  {pendingAction === AFTER_SAVE_ACTION.SIGN_ZONE
                    ? "Đang mở..."
                    : "Ký số"}
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </div>
      <AgreementPdfModal
        isOpen={modalAgreementPdf}
        toggle={toggleAgreementPdfModal}
        invoiceId={invoiceId}
        typeReport={typeReport}
        agreementId={rawData?.agreement_id}
        agreementInfo={rawData}
      />
      <AgreementSignPaperModal
        isOpen={modalSignPaper}
        toggle={toggleSignPaperModal}
        invoiceId={invoiceId}
        typeReport={typeReport}
        agreementId={rawData?.agreement_id}
        agreementInfo={rawData}
        onAttached={refreshAgreementList}
      />
      <AgreementSignZoneModal
        isOpen={modalSignZone}
        toggle={toggleSignZoneModal}
        invoiceId={invoiceId}
        typeReport={typeReport}
        rawData={rawDataForSign}
        onSaved={refreshAgreementList}
      />
    </Modal>
  );
};

AgreementReportModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  invoiceId: PropTypes.number,
  typeReport: PropTypes.string,
  onCreateSuccess: PropTypes.func,
};

export default AgreementReportModal;
